import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import SimpleLineChart from '../components/charts/SimpleLineChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// type sent to /api/azure/recommendations, keyed by sub-tab index
const TAB_TYPES = ['COMBINED_BLOB_STORAGE', 'CUSTOM_BLOB_STORAGE', 'BLOB_STORAGE'];

// ── Rec helpers ─────────────────────────────────────────────

const resolveBlobRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(t)) recPillClass = 'rec-id';
  else if (['TIER', 'DOWNGRADE', 'RIGHTSIZE', 'MOVETOCOOL', 'COOL'].includes(t)) recPillClass = 'rec-rs';
  else if (['ARCHIVE', 'MOVETOARCHIVE'].includes(t)) recPillClass = 'rec-tier';
  return { recType: t || 'OPTIMAL', recPillClass };
};

// ── Row Mapper ──────────────────────────────────────────────
// All three tabs (COMBINED / CUSTOM / BLOB_STORAGE) return the same item shape.

const toBlobRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const currentTier     = item.currentAccessTier ?? '—';
  const recommendedTier = item.recommendedAccessTier ?? '—';
  const tierChange       = currentTier !== '—' && recommendedTier !== '—' &&
    currentTier.toUpperCase() !== recommendedTier.toUpperCase();

  // The API sends recommendationType (e.g. OPTIMAL). When it is OPTIMAL but the
  // recommended tier differs from the current tier, surface the tiering move.
  const rawRec = tierChange && (item.recommendationType ?? 'OPTIMAL').toUpperCase() === 'OPTIMAL'
    ? 'MOVETOCOOL'
    : (item.recommendationType ?? 'OPTIMAL');
  const { recType, recPillClass } = resolveBlobRec(rawRec);

  const statsUnavailable = item.blobStatsUnavailable === true;
  const lastAccessDays   = statsUnavailable ? null : (item.lastAccessDays ?? null);

  const remark = recType === 'MOVETOCOOL' && tierChange
    ? `Move ${currentTier} → ${recommendedTier}`
    : (recType === 'OPTIMAL' ? 'Optimal' : recType);

  return {
    id: item.id,
    name: item.blobStorageName ?? '—',
    location: item.location ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    redundancy: item.storageSkuName ?? item.skuName ?? '—',
    kind: item.kind ?? '—',
    currentTier,
    recommendedTier,
    tierChange,
    sizeGB: Number(item.totalBlobSizeGB ?? 0),
    blobCount: Number(item.totalBlobCount ?? 0),
    statsUnavailable,
    lastAccessDays,
    timeCreated: item.timeCreated ?? null,
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    savingValue: saving,
    recommendedBy: item.recommendedBy ?? '—',
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapRows = (data) => (data ?? []).map(toBlobRow);

// ── KPI ─────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec     = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving  = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const tieringCandidates = rows.filter((r) => r.tierChange);

  return buildBaseCostKpis({
    countTitle: 'Storage Accounts',
    countValue: rows.length,
    countSubtitle: `${rows.reduce((s, r) => s + r.blobCount, 0).toLocaleString()} blobs · ${rows.reduce((s, r) => s + r.sizeGB, 0).toFixed(1)} GB`,
    current, saving, recommended,
    kpi4Title: 'Tiering Candidates',
    kpi4Value: `${tieringCandidates.length}`,
    kpi4Subtitle: `account${tieringCandidates.length !== 1 ? 's' : ''} with a cheaper tier available`,
  });
};

// ── Charts (all derived from API rows) ──────────────────────

const TIER_COLORS = { HOT: '#2196f3', COOL: '#f57c00', ARCHIVE: '#8e24aa', UNKNOWN: '#9aa3b7' };

// Group rows by a field; value = summed cost when any cost exists, else count.
const groupBy = (rows, keyFn, valFn) => {
  const hasVal = rows.some((r) => valFn(r) > 0);
  const map = {};
  rows.forEach((r) => {
    const k = keyFn(r);
    if (!k || k === '—') return;
    map[k] = (map[k] || 0) + (hasVal ? valFn(r) : 1);
  });
  return { map, hasVal };
};

const buildTierDonut = (rows) => {
  if (!rows.length) return null;
  const { map, hasVal } = groupBy(rows, (r) => (r.currentTier ?? 'Unknown').toUpperCase(), (r) => r.costValue);
  const labels = Object.keys(map);
  if (!labels.length) return null;
  return {
    _unit: hasVal ? 'cost' : 'count',
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(map[k].toFixed(2))),
      backgroundColor: labels.map((k) => TIER_COLORS[k] ?? TIER_COLORS.UNKNOWN),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

const buildRegionBar = (rows) => {
  if (!rows.length) return null;
  const { map, hasVal } = groupBy(rows, (r) => r.location, (r) => r.costValue);
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.length) return null;
  return {
    labels: sorted.map(([k]) => k),
    datasets: [{
      label: hasVal ? 'Cost ($)' : 'Accounts',
      data: sorted.map(([, v]) => parseFloat(v.toFixed(2))),
      backgroundColor: '#0078d4',
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

// Storage growth — last 30 days. Driven by the BLOB_STORAGE_TREND API which
// returns { Hot: {date: cost}, Cool: {...}, Archive: {...} } (empty tiers omitted).
const TREND_TIER_ORDER = ['Hot', 'Cool', 'Archive'];

const buildTrendLine = (byTier) => {
  if (!byTier) return null;
  const allDates = new Set();
  TREND_TIER_ORDER.forEach((tier) => Object.keys(byTier[tier] ?? {}).forEach((d) => allDates.add(d)));
  const dates = [...allDates].sort();
  if (!dates.length) return null;

  const datasets = TREND_TIER_ORDER
    .filter((tier) => byTier[tier] && Object.keys(byTier[tier]).length)
    .map((tier) => {
      const color = TIER_COLORS[tier.toUpperCase()] ?? TIER_COLORS.UNKNOWN;
      return {
        label: tier,
        data: dates.map((d) => parseFloat(Number(byTier[tier][d] ?? 0).toFixed(4))),
        borderColor: color,
        backgroundColor: color + '26',
        tension: 0.3,
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
      };
    });
  if (!datasets.length) return null;

  return {
    labels: dates.map((d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets,
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Tier Hot → Cool': 0, 'Archive cold data': 0, 'Delete stale': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (['ARCHIVE', 'MOVETOARCHIVE'].includes(r.recType)) buckets['Archive cold data'] += s;
    else if (['DELETE', 'REMOVEUNUSEDRESOURCE', 'REMOVE'].includes(r.recType)) buckets['Delete stale'] += s;
    else buckets['Tier Hot → Cool'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{
      label: 'Saving ($)',
      data: Object.values(buckets).map((v) => parseFloat(v.toFixed(2))),
      backgroundColor: ['#f57c00', '#8e24aa', '#ef4444'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows
    .filter((r) => r.savingValue > 0)
    .sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →'; let actionClass = 'mini-btn';
    if (['ARCHIVE', 'MOVETOARCHIVE'].includes(r.recType)) { actionLabel = 'Archive →'; }
    else if (['DELETE', 'REMOVEUNUSEDRESOURCE'].includes(r.recType)) { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (['TIER', 'MOVETOCOOL', 'COOL', 'DOWNGRADE'].includes(r.recType)) { actionLabel = 'Tier →'; }
    return {
      id: i + 1,
      title: r.name || r.id,
      amount: r.saving,
      desc: `${r.currentTier} · ${r.redundancy} · ${r.location}`,
      actionLabel,
      actionClass,
      row: r,
    };
  });
  return { actions, totalSaving };
};

// ── Table Columns ────────────────────────────────────────────

const tierPillClass = (tier) => {
  const t = (tier ?? '').toUpperCase();
  if (t === 'HOT')     return 'st-running';
  if (t === 'COOL')    return 'st-stopped';
  if (t === 'ARCHIVE') return 'st-unknown';
  return 'st-unknown';
};

const blobColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'name',          label: 'Account Name' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'redundancy',    label: 'SKU' },
  {
    key: 'currentTier', label: 'Current Tier',
    render: (row) => <span className={`status-pill ${tierPillClass(row.currentTier)}`}>{row.currentTier}</span>,
  },
  {
    key: 'recommendedTier', label: 'Recommended Tier',
    render: (row) =>
      row.tierChange
        ? <span className={`status-pill ${tierPillClass(row.recommendedTier)}`}>{row.recommendedTier}</span>
        : <span style={{ color: 'var(--muted)' }}>—</span>,
  },
  {
    key: 'sizeGB', label: 'Size (GB)', className: 'num',
    render: (row) => <span title={`${row.blobCount.toLocaleString()} blobs`}>{row.sizeGB.toFixed(2)}</span>,
  },
  {
    key: 'lastAccessDays', label: 'Last Access',
    render: (row) =>
      row.statsUnavailable ? <span style={{ color: 'var(--muted)' }}>N/A</span>
        : (row.lastAccessDays != null ? `${row.lastAccessDays} d ago` : '—'),
  },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const BlobStorageOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAzure,    setIsLoadingAzure]    = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [azureRows,    setAzureRows]    = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [azureKpi,    setAzureKpi]    = useState([]);

  const [tierDonutData,   setTierDonutData]   = useState(null);
  const [regionBarData,   setRegionBarData]   = useState(null);
  const [savingsByAction, setSavingsByAction] = useState(null);
  const [trendData,       setTrendData]       = useState(null);
  const [isLoadingTrend,  setIsLoadingTrend]  = useState(false);
  const [insightActions,  setInsightActions]  = useState([]);
  const [insightTotal,    setInsightTotal]    = useState(0);

  const [tagList,          setTagList]          = useState([]);
  const [filterList,       setFilterList]       = useState({ tag: '', values: [] });
  const [locationFilter,   setLocationFilter]   = useState('');
  const [tierFilter,       setTierFilter]       = useState('');
  const [redundancyFilter, setRedundancyFilter] = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      accountId: row.id, accountName: row.name, location: row.location,
      resourceGroup: row.resourceGroup, redundancy: row.redundancy,
      currentTier: row.currentTier, recommendedTier: row.recommendedTier,
      sizeGB: row.sizeGB, blobCount: row.blobCount, lastAccessDays: row.lastAccessDays,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the data provided, give a short and natural insight ' +
      'as if you are speaking directly to the user — not reading a report. ' +
      'Mention the cost situation, what savings are within reach, and what the user should consider doing. ' +
      'Keep it conversational, friendly, and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'BLOB_STORAGE', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  // Storage-growth trend is account-level (not tied to a recommendation source),
  // so it is fetched once per account rather than per sub-tab.
  const fetchTrendData = (userId) => {
    setIsLoadingTrend(true);
    setTrendData(null);
    RecommendationApiService.getAzureData({ userId, type: 'BLOB_STORAGE_TREND', userAccountId: selectedAccount })
      .then((res) => setTrendData(buildTrendLine(res?.data)))
      .catch((err) => console.error('Error fetching blob storage trend', err))
      .finally(() => setIsLoadingTrend(false));
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchCombinedData = (userId) => {
    setIsLoadingCombined(true);
    setCombinedRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[0], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching combined blob recommendations', err))
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId) => {
    setIsLoadingCustom(true);
    setCustomRows([]);
    RecommendationApiService.getAzureData({
      userId, type: TAB_TYPES[1], userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching custom blob recommendations', err))
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId) => {
    setIsLoadingAzure(true);
    setAzureRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[2], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching azure blob recommendations', err))
      .finally(() => setIsLoadingAzure(false));
  };

  // Lazy fetch: only hits the API for a tab the first time it is opened.
  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0) fetchCombinedData(userId);
    else if (tabIndex === 1) fetchCustomData(userId);
    else if (tabIndex === 2) fetchAzureData(userId);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    fetchedTabsRef.current.delete(activeTab);
    fetchTabData(activeTab, true);
    fetchTagList();
    const userId = getUserId();
    if (userId) fetchTrendData(userId);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      setActiveTab(0);
      fetchTabData(0);
      fetchTagList();
      const userId = getUserId();
      if (userId) fetchTrendData(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // CloudsAnalytics (custom) tag filters are server-side — refetch only while its tab is active.
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedTabsRef.current.add(1);
        fetchCustomData(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const locations    = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const tiers        = useMemo(() => [...new Set(baseRows.map((r) => r.currentTier).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const redundancies = useMemo(() => [...new Set(baseRows.map((r) => r.redundancy).filter((v) => v && v !== '—'))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = baseRows;
    if (locationFilter)   rows = rows.filter((r) => r.location === locationFilter);
    if (tierFilter)       rows = rows.filter((r) => (r.currentTier ?? '').toUpperCase() === tierFilter.toUpperCase());
    if (redundancyFilter) rows = rows.filter((r) => r.redundancy === redundancyFilter);
    return rows;
  }, [baseRows, locationFilter, tierFilter, redundancyFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setTierDonutData(buildTierDonut(filteredRows));
      setRegionBarData(buildRegionBar(filteredRows));
      setSavingsByAction(buildSavingsByAction(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setTierDonutData(null); setRegionBarData(null); setSavingsByAction(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with what's on screen
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Blob Storage Optimization',
      kpi: activeKpi,
      blobRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi]);

  const handleExport = () =>
    exportCSV(activeRows, blobColumns.filter((c) => c.key !== 'action'), `blob-storage-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setLocationFilter(''); setTierFilter(''); setRedundancyFilter('');
    setFilterList({ tag: '', values: [] });
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Blob Storage</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Blob Storage Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} accounts</span>}
          </h1>
          <div className="meta">Move cold data to cheaper tiers, enforce lifecycle policies, delete stale blobs</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="Storage by access tier" subtitle="Hot / Cool / Archive split">
          <DonutChart data={tierDonutData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Storage by region" subtitle="Where your accounts live">
          <HorizontalBarChart data={regionBarData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard
          title="Top blob actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="accounts"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Sub-tabs: Combined / CloudsAnalytics / Azure */}
      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Access Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="">All</option>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>SKU</label>
          <select value={redundancyFilter} onChange={(e) => setRedundancyFilter(e.target.value)}>
            <option value="">All</option>
            {redundancies.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="spacer" />
        {(filterList.tag || locationFilter || tierFilter || redundancyFilter) && (
          <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>
        )}
        <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
      </div>

      <Table
        rows={activeRows}
        columns={blobColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No blob storage accounts found."
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Storage growth — last 30 days" subtitle="Daily storage spend by access tier">
          <SimpleLineChart data={trendData} loading={isLoadingTrend} yTickCallback={(v) => '$' + v} />
        </Card>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsByAction} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default BlobStorageOptimization;
